import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo-kinshasa.png';

const Sidebar = ({ activeSection, setActiveSection, onLogout, user }) => {
  const navigate = useNavigate();
  const [inputsMenuOpen, setInputsMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const menuItems = [
    { id: 'statistiques', icon: 'fa-chart-bar', label: 'Statistiques' },
    { id: 'enrolement', icon: 'fa-user-plus', label: 'Enrôlement Agent' },
    { id: 'recherche', icon: 'fa-search', label: 'Rechercher' },
    { id: 'import', icon: 'fa-file-import', label: 'Import CSV' },
    { id: 'export', icon: 'fa-file-export', label: 'Export' },
    { id: 'bureaux', icon: 'fa-building', label: 'Bureaux' },
    { id: 'cellules', icon: 'fa-sitemap', label: 'Cellules' },
    { id: 'utilisateurs', icon: 'fa-users', label: 'Utilisateurs', adminOnly: true },
    { id: 'profil', icon: 'fa-user-circle', label: 'Mon Profil' },
  ].filter((item) => {
    // Filtrer les éléments admin-only si l'utilisateur n'est pas admin
    if (item.adminOnly && user?.role !== 'admin') {
      return false;
    }
    return true;
  });

  const inputsSubMenus = [
    { id: 'grades', icon: 'fa-graduation-cap', label: 'Grades' },
    { id: 'fonctions', icon: 'fa-briefcase', label: 'Fonctions' },
    { id: 'categories-professionnelles', icon: 'fa-tags', label: 'Catégories Professionnelles' },
  ];

  // Vérifier si une section d'inputs est active
  const isInputsSectionActive = inputsSubMenus.some(subMenu => activeSection === subMenu.id);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  return (
    <aside className={`${isCollapsed ? 'w-20' : 'w-72'} bg-cardBg shadow-xl flex flex-col animate-slide-in transition-all duration-300 relative`}>
      {/* Bouton pour réduire/agrandir */}
      <button
        onClick={() => {
          setIsCollapsed(!isCollapsed);
          if (!isCollapsed) {
            setInputsMenuOpen(false); // Fermer le menu Inputs quand on réduit
          }
        }}
        className="absolute -right-3 top-6 z-10 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center shadow-lg hover:bg-primary-dark transition-all"
        title={isCollapsed ? 'Agrandir le menu' : 'Réduire le menu'}
      >
        <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'left'} text-xs`}></i>
      </button>

      <div className={`p-6 border-b border-gray-200 ${isCollapsed ? 'px-2' : ''}`}>
        <div className="flex items-center justify-center mb-4">
          <img src={logo} alt="Logo MPTMU" className={`${isCollapsed ? 'w-12 h-12' : 'w-20 h-20'} object-contain transition-all duration-300`} />
        </div>
        {!isCollapsed && (
          <>
            <h1 className="text-xl font-bold text-center text-primary">IdTrack</h1>
            <p className="text-xs text-center text-text-light mt-1">
              Ministère Provincial de Transport et Mobilité Urbaine
            </p>
          </>
        )}
      </div>

      {/* Informations utilisateur */}
      {user && (
        <div className={`${isCollapsed ? 'px-2' : 'px-4'} py-4 border-b border-gray-200 bg-gray-50`}>
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
            <div className={`${isCollapsed ? 'w-10 h-10' : 'w-12 h-12'} bg-primary rounded-full flex items-center justify-center text-white font-bold ${isCollapsed ? '' : 'mr-3'} transition-all duration-300`}>
              {user.nom?.[0]?.toUpperCase() || user.username?.[0]?.toUpperCase() || 'U'}
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text truncate">
                  {user.nom} {user.prenom}
                </p>
                <p className="text-xs text-text-light truncate">@{user.username}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <nav className="flex-1 py-6 overflow-y-auto">
        <ul className={`space-y-2 ${isCollapsed ? 'px-2' : 'px-4'}`}>
          {menuItems.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => setActiveSection(item.id)}
                className={`w-full text-left group ${
                  activeSection === item.id ? 'bg-primary text-white' : ''
                }`}
                title={isCollapsed ? item.label : ''}
              >
                <div
                  className={`flex items-center ${isCollapsed ? 'justify-center' : ''} p-3 rounded-lg transition-all duration-300 ${
                    activeSection === item.id
                      ? 'bg-primary text-white'
                      : 'hover:bg-primary hover:text-white'
                  }`}
                >
                  <i className={`fas ${item.icon} ${isCollapsed ? 'w-5' : 'w-5 text-center mr-3'}`}></i>
                  {!isCollapsed && <span className="font-medium">{item.label}</span>}
                </div>
              </button>
            </li>
          ))}
          
          {/* Menu Inputs avec sous-menus */}
          {!isCollapsed && (
            <li>
              <div>
                <button
                  onClick={() => setInputsMenuOpen(!inputsMenuOpen)}
                  className={`w-full text-left group ${
                    isInputsSectionActive ? 'bg-primary text-white' : ''
                  }`}
                >
                  <div
                    className={`flex items-center justify-between p-3 rounded-lg transition-all duration-300 ${
                      isInputsSectionActive
                        ? 'bg-primary text-white'
                        : 'hover:bg-primary hover:text-white'
                    }`}
                  >
                    <div className="flex items-center">
                      <i className="fas fa-database w-5 text-center mr-3"></i>
                      <span className="font-medium">Inputs</span>
                    </div>
                    <i
                      className={`fas fa-chevron-${inputsMenuOpen ? 'down' : 'right'} text-xs transition-transform duration-300`}
                    ></i>
                  </div>
                </button>
                {inputsMenuOpen && (
                  <ul className="ml-4 mt-1 space-y-1">
                    {inputsSubMenus.map((subMenu) => (
                      <li key={subMenu.id}>
                        <button
                          onClick={() => setActiveSection(subMenu.id)}
                          className={`w-full text-left p-2 rounded-lg transition-all duration-300 ${
                            activeSection === subMenu.id
                              ? 'bg-primary text-white'
                              : 'hover:bg-gray-100 text-text'
                          }`}
                        >
                          <div className="flex items-center">
                            <i className={`fas ${subMenu.icon} w-4 text-center mr-2`}></i>
                            <span className="text-sm">{subMenu.label}</span>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          )}
          
          {/* Menu Inputs réduit - affiche les sous-menus directement */}
          {isCollapsed && inputsSubMenus.map((subMenu) => (
            <li key={subMenu.id}>
              <button
                onClick={() => setActiveSection(subMenu.id)}
                className={`w-full text-left group ${
                  activeSection === subMenu.id ? 'bg-primary text-white' : ''
                }`}
                title={subMenu.label}
              >
                <div
                  className={`flex items-center justify-center p-3 rounded-lg transition-all duration-300 ${
                    activeSection === subMenu.id
                      ? 'bg-primary text-white'
                      : 'hover:bg-primary hover:text-white'
                  }`}
                >
                  <i className={`fas ${subMenu.icon} w-5`}></i>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className={`${isCollapsed ? 'p-2' : 'p-4'} border-t border-gray-200`}>
        <button
          onClick={handleLogout}
          className={`w-full ${isCollapsed ? 'px-2 py-2' : 'px-4 py-2'} bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all mb-4 flex items-center ${isCollapsed ? 'justify-center' : ''}`}
          title={isCollapsed ? 'Déconnexion' : ''}
        >
          <i className="fas fa-sign-out-alt"></i>
          {!isCollapsed && <span className="ml-2">Déconnexion</span>}
        </button>
        {!isCollapsed && (
          <div className="text-xs text-center text-text-light">
            <p>Version 1.0.0</p>
            <p className="mt-1">© 2025 MPTMU Kinshasa</p>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;

